/**
 * A minimal ZIP writer — enough to bundle a set of already-compressed files
 * (photographs) into one download.
 *
 * Deliberately dependency-free, for the same reason the CSV/PDF exports next
 * door are: the portal ships no archive library, and pulling one in to staple
 * a few hundred JPEGs together is not a trade worth making.
 *
 * Every entry is STORED (method 0), never deflated. JPEG and PNG are already
 * compressed — deflating them costs CPU and saves nothing — and storing means
 * the whole format is four fixed-layout records with a CRC, which is small
 * enough to read and verify here.
 *
 * Limits, stated rather than guarded against silently: the classic (non-Zip64)
 * format tops out at 65,535 entries and 4 GiB. buildZip() throws rather than
 * writing an archive that would open empty.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

/** MS-DOS packed time/date — the only timestamp the base format carries. */
function dosTime(d: Date): number {
  return ((d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1)) & 0xffff;
}

function dosDate(d: Date): number {
  // The DOS epoch is 1980; anything earlier cannot be represented at all.
  const year = Math.max(1980, d.getFullYear());
  return (((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate()) & 0xffff;
}

export type ZipEntry = {
  /** Path inside the archive. Forward slashes make folders. */
  name: string;
  data: Uint8Array;
};

const MAX_ENTRIES = 0xffff;
const MAX_BYTES = 0xffffffff;

export function buildZip(entries: ZipEntry[], at: Date = new Date()): Blob {
  if (entries.length > MAX_ENTRIES) {
    throw new Error(
      `A zip file can hold ${MAX_ENTRIES.toLocaleString()} entries; ${entries.length.toLocaleString()} were given.`,
    );
  }

  const encoder = new TextEncoder();
  const time = dosTime(at);
  const date = dosDate(at);

  const parts: BlobPart[] = [];
  const directory: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const size = entry.data.length;
    const crc = crc32(entry.data);

    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true); // local file header signature
    lv.setUint16(4, 20, true); // version needed to extract (2.0)
    lv.setUint16(6, 0x0800, true); // flags: bit 11 = the name is UTF-8
    lv.setUint16(8, 0, true); // method 0 — stored
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true); // compressed size == uncompressed, stored
    lv.setUint32(22, size, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true); // no extra field
    local.set(name, 30);

    parts.push(local, entry.data);

    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true); // central directory header signature
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, name.length, true);
    // extra length (30), comment length (32), disk (34), internal attrs (36)
    // and external attrs (38) are all zero — the buffer is already zeroed.
    cv.setUint32(42, offset, true); // where this entry's local header starts
    central.set(name, 46);
    directory.push(central);

    offset += local.length + size;

    if (offset > MAX_BYTES) {
      throw new Error(
        "This selection is over 4 GB, which is more than a plain zip file can hold. " +
          "Export it in smaller batches.",
      );
    }
  }

  const directorySize = directory.reduce((total, record) => total + record.length, 0);

  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true); // end of central directory signature
  ev.setUint16(8, entries.length, true); // entries on this disk
  ev.setUint16(10, entries.length, true); // entries in total
  ev.setUint32(12, directorySize, true);
  ev.setUint32(16, offset, true); // where the central directory starts

  parts.push(...directory, end);

  return new Blob(parts, { type: "application/zip" });
}

/** Hand a built blob to the browser as a download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on a later tick — Safari cancels the download if the URL dies
  // before it has finished reading it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
